/**
 * Utility for managing new product notifications using localStorage
 */

const STORAGE_KEY = 'practice_products_seen';

export interface ProductNotificationState {
  productIds: string[];
  lastUpdated: string;
}

/**
 * Get the current stored product IDs from localStorage
 */
export function getStoredProductIds(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return [];
    }
    
    const data: ProductNotificationState = JSON.parse(stored);
    return data.productIds || [];
  } catch (error) {
    console.warn('Failed to parse stored product IDs:', error);
    return [];
  }
}

/**
 * Store the current product IDs to localStorage
 */
export function storeProductIds(productIds: string[]): void {
  try {
    const data: ProductNotificationState = {
      productIds,
      lastUpdated: new Date().toISOString(),
    };
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to store product IDs:', error);
  }
}

/**
 * Compare current products with stored products and return new product IDs
 */
export function getNewProductIds(currentProducts: Record<string, string>): string[] {
  const currentIds = Object.keys(currentProducts);
  const storedIds = getStoredProductIds();
  
  // Find product IDs that are in current but not in stored
  const newIds = currentIds.filter(id => !storedIds.includes(id));
  
  return newIds;
}

/**
 * Check if there are new products and update storage
 * Returns the new product IDs if any, empty array if none
 */
export function checkForNewProducts(currentProducts: Record<string, string>): string[] {
  const newIds = getNewProductIds(currentProducts);
  
  // If there are new products, update storage with all current products
  if (newIds.length > 0) {
    const allCurrentIds = Object.keys(currentProducts);
    storeProductIds(allCurrentIds);
  }
  
  return newIds;
}

/**
 * Initialize storage with current products (for first-time users)
 * This should be called when user has no stored data
 */
export function initializeProductStorage(currentProducts: Record<string, string>): void {
  const currentIds = Object.keys(currentProducts);
  storeProductIds(currentIds);
}

/**
 * Clear stored product data (useful for testing or logout)
 */
export function clearProductStorage(): void {
  localStorage.removeItem(STORAGE_KEY);
}
